import { useUi, useUiDispatch } from "@/app/AppContext";
import { SimpleMarkdown } from "@/components/SimpleMarkdown";
import { getVisibleExtraSections } from "@/domain/detailSections";
import {
  getCriterionStatusesByLocation,
  getScoreNumberForStatus,
  normalizeSystemStatus,
  SYSTEM_SCORE_SCALE,
  type SystemAnalysis,
  type SystemCriterion,
  type SystemDomain,
} from "@/domain/systemAnalysis";
import { stripDiacritics } from "@/domain/text";
import type { ExtraSectionStyle } from "@/domain/types";
import { useI18n } from "@/i18n/useI18n";
import { pick, statusLabelKey } from "./useSystemAnalysis";

/** Section titles already rendered as fixed fields of the panel. */
const RESERVED_TITLES = new Set([
  "systemomrade",
  "system domain",
  "kriterium",
  "criterion",
  "kriterieum",
  "vurdering",
  "assessment",
  "vurderdering",
  "grunnlag",
  "evidence",
  "detaljer",
  "details",
]);
const SECTION_CLASS: Record<ExtraSectionStyle, string> = {
  default: "",
  m: " muted",
  h: " highlight",
  t: " text-only",
};

interface SystemDetailPanelProps {
  analysis: SystemAnalysis | null;
  domain: SystemDomain;
  item: SystemCriterion;
  isAllLocations: boolean;
}

export function SystemDetailPanel({
  analysis,
  domain,
  item,
  isAllLocations,
}: SystemDetailPanelProps) {
  const { t, language } = useI18n();
  const { detailsPosition, system } = useUi();
  const dispatch = useUiDispatch();

  const status = normalizeSystemStatus(item.status);
  const statuses = item.statuses?.length ? item.statuses.map(normalizeSystemStatus) : [status];
  const currentScores = new Set(statuses.map(getScoreNumberForStatus).filter(Boolean));
  const primary = language === "en" ? item.scoreTextsEn : item.scoreTextsNo;
  const fallback = language === "en" ? item.scoreTextsNo : item.scoreTextsEn;
  const scale = SYSTEM_SCORE_SCALE.map((entry) => ({
    ...entry,
    text: (primary?.[entry.number] || fallback?.[entry.number] || "").trim(),
    isCurrent: currentScores.has(entry.number),
  }))
    .filter((row) => row.text)
    // "Not applicable" (score 0) is always listed last.
    .sort((a, b) => Number(a.number === "0") - Number(b.number === "0"));
  const hasScale = scale.length > 1;
  const visibleScale = hasScale && system.showScale ? scale : scale.filter((row) => row.isCurrent);

  const evidence = pick(language, item.evidenceNo, item.evidenceEn).trim();
  const sections = getVisibleExtraSections(item.extraSections ?? [], language).filter(
    (section) =>
      section.title &&
      section.text &&
      !RESERVED_TITLES.has(stripDiacritics(section.title).toLowerCase().trim()),
  );
  const source = item.sources[0]
    ? pick(language, item.sources[0].labelNo, item.sources[0].labelEn)
    : t("ingen");
  const meta = (key: string) =>
    pick(language, item.metadata[`${key}No`], item.metadata[`${key}En`]) || t("ikkeAngitt");
  const moveToRight = detailsPosition !== "right";
  const layoutLabel = moveToRight ? t("moveDetailsRight") : t("moveDetailsBelow");

  return (
    <div className="details-card">
      <div className="details-top-row">
        <div className="details-title-row">
          <span className={`details-id-badge system-status-${status}`}>{item.id}</span>
          <div className="details-title">{pick(language, item.titleNo, item.titleEn)}</div>
        </div>
        <div className="details-header-actions">
          <button
            type="button"
            className="details-layout-toggle-btn"
            aria-label={layoutLabel}
            title={layoutLabel}
            onClick={() => dispatch({ type: "toggleDetailsPosition" })}
          >
            {moveToRight ? "→" : "↓"}
          </button>
          <button
            type="button"
            className="details-close-btn"
            aria-label={t("systemCloseDetails")}
            onClick={() => dispatch({ type: "closeSystemDetail" })}
          >
            ×
          </button>
        </div>
      </div>
      <div className="system-detail-status">
        <span className={`system-status-dot system-status-${status}`} />
        {t(statusLabelKey(status))}
      </div>

      <Field
        label={t("systemDomain")}
      >{`${domain.id}. ${pick(language, domain.titleNo, domain.titleEn)}`}</Field>
      <Field label={t("systemCriterion")}>
        {pick(language, item.criterionNo, item.criterionEn)}
      </Field>
      <div className="system-detail-section">
        <div className="system-detail-label-row">
          <div className="system-detail-label">{t("systemAssessment")}</div>
          {hasScale && (
            <label className="system-assessment-toggle">
              <input
                type="checkbox"
                checked={system.showScale}
                onChange={() => dispatch({ type: "toggleSystemScale" })}
              />
              <span className="system-assessment-toggle-track" aria-hidden="true" />
              <span className="system-assessment-toggle-label">{t("systemToggleScale")}</span>
            </label>
          )}
        </div>
        {visibleScale.length ? (
          <div className="system-assessment-scale">
            {visibleScale.map((row) => (
              <div
                key={row.number}
                className={`system-assessment-scale-row${row.isCurrent ? " is-current" : ""}`}
              >
                <span
                  className={`system-status-dot system-status-${row.status}`}
                  aria-hidden="true"
                />
                <span className="system-assessment-scale-text">
                  <strong>{t(statusLabelKey(row.status))}:</strong> {row.text}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="system-detail-value">
            <SimpleMarkdown text={pick(language, item.assessmentNo, item.assessmentEn)} />
          </div>
        )}
      </div>
      {evidence && (
        <div className="system-detail-section">
          <div className="system-detail-value">
            <SimpleMarkdown text={evidence} />
          </div>
        </div>
      )}

      {sections.length ? (
        <div className="details-extra-box">
          {sections.map((section, index) => (
            <div
              key={`${index}:${section.title}`}
              className={`details-extra-section${SECTION_CLASS[section.style]}`}
            >
              <div className="details-extra-title">{section.title}</div>
              <div className="details-extra-body">
                <SimpleMarkdown text={section.text} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <MetaBox label={t("systemSource")} value={source} />
          <MetaBox label={t("systemSector")} value={meta("sector")} />
          <MetaBox label={t("systemInterventionType")} value={meta("interventionType")} />
          <MetaBox label={t("systemInterventionReach")} value={meta("interventionReach")} />
        </>
      )}

      {isAllLocations && <LocationStatuses analysis={analysis} domain={domain} item={item} />}
    </div>
  );
}

function LocationStatuses({
  analysis,
  domain,
  item,
}: {
  analysis: SystemAnalysis | null;
  domain: SystemDomain;
  item: SystemCriterion;
}) {
  const { t, language } = useI18n();
  const dispatch = useUiDispatch();
  const rows = getCriterionStatusesByLocation(analysis, item.id, language);
  return (
    <div className="system-location-details-box">
      <div className="details-extra-title">{t("systemDetailsBoxTitle")}</div>
      {rows.length ? (
        rows.map(({ location, status }) => {
          const name =
            (language === "en" ? location.locationName.en : location.locationName.no) ||
            location.locationId;
          const label = t(statusLabelKey(status));
          return (
            <button
              key={location.locationId}
              type="button"
              className="system-location-details-row system-location-details-btn"
              aria-label={`${name} (${label})`}
              onClick={() => {
                dispatch({ type: "selectSystemLocation", locationId: location.locationId });
                dispatch({
                  type: "selectSystemCriterion",
                  domainId: domain.id,
                  criterionId: item.id,
                });
              }}
            >
              <span className="system-location-details-left">
                <span className={`system-status-dot system-status-${status}`} aria-hidden="true" />
                <span className="system-location-details-name">{name}</span>
              </span>
              <span className="system-location-details-right">
                {label}
                <span className="system-location-details-action" aria-hidden="true">
                  ›
                </span>
              </span>
            </button>
          );
        })
      ) : (
        <div className="system-detail-value">{t("systemNoLocationScores")}</div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: string }) {
  return (
    <div className="system-detail-section">
      <div className="system-detail-label">{label}</div>
      <div className="system-detail-value">{children}</div>
    </div>
  );
}

function MetaBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="system-meta-box">
      <Field label={label}>{value}</Field>
    </div>
  );
}
